import React from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import makeInitials from '../../lib/makeInitials';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { SIGNATURE_FONTS } from '../../common/constants';
import { colors } from '@skyslope/mache';
import { ISignatureSettings, ISigner } from '../../store/types';

const useStyles = makeStyles((theme: Theme) => ({
  inputs: {
    display: 'flex',
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  signatureInput: {
    flexGrow: 3,
    marginRight: 16,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  initialsInput: {
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      marginTop: 16,
    },
  },
  fontOptions: {
    marginTop: 16,
  },
  fontOption: {
    width: '100%',
    '&:not(:first-child)': {
      borderTop: `1px solid ${colors.grey[400]}`,
    },
  },
  fontOptionLabel: {
    width: '100%',
  },
  fontOptionText: {
    fontSize: 24,
    color: colors.grey[800],
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    [theme.breakpoints.down('xs')]: {
      padding: '16px 0',
    },
  },
  fontOptionTextActive: {
    color: colors.blue[800],
  },
}));

interface IProps {
  signatureSettings: ISignatureSettings;
  setSignatureSettings: (signatureSettings: ISignatureSettings) => void;
  defaultName: string;
  signer: ISigner;
}

const ChooseStyle = (props: IProps) => {
  const { signatureSettings, setSignatureSettings, signer } = props;
  const classes = useStyles();

  React.useEffect(() => {
    props.setSignatureSettings({
      signatureValue: props.defaultName,
      initialsValue: makeInitials(props.defaultName),
      signatureType: 'text',
      signatureFont: signer.signingSettings?.signatureFont || undefined,
    });
  }, []);

  const updateSignature = (e: any) => {
    const signatureValue = e.target.value;
    const initialsValue = makeInitials(signatureValue);
    setSignatureSettings({
      ...signatureSettings,
      signatureValue,
      initialsValue,
    });
  };

  const updateInitials = (e: any) => {
    setSignatureSettings({ ...signatureSettings, initialsValue: e.target.value });
  };

  const updateFont = (e: any) => {
    setSignatureSettings({ ...signatureSettings, signatureFont: e.target.value });
  };

  return (
    <>
      <div className={classes.inputs}>
        <TextField
          className={classes.signatureInput}
          variant="filled"
          label="Your Full Name"
          value={signatureSettings.signatureValue}
          onChange={updateSignature}
          autoComplete="skyslope-prevent-autocomplete"
          name="name"
          inputProps={{
            'data-spec': 'signature-input',
          }}
        />
        <TextField
          className={classes.initialsInput}
          variant="filled"
          label="Your Initials"
          value={signatureSettings.initialsValue}
          onKeyDown={e => {
            if (e.keyCode === 32) {
              e.preventDefault();
            }
          }}
          onChange={updateInitials}
          autoComplete="skyslope-prevent-autocomplete-initials"
          name="initials"
          inputProps={{
            'data-spec': 'initials-input',
          }}
        />
      </div>
      <Typography variant="body1">
        Type your full name above and choose a style below to set how your signature and initials will look on this
        document.
      </Typography>
      <RadioGroup
        className={classes.fontOptions}
        aria-label="signature font"
        value={signatureSettings.signatureFont}
        onChange={updateFont}
        data-spec="font-options"
      >
        {Object.keys(SIGNATURE_FONTS).map(fontName => (
          <FormControlLabel
            key={fontName}
            classes={{
              root: classes.fontOption,
              label: classes.fontOptionLabel,
            }}
            value={fontName}
            control={<Radio color="primary" />}
            aria-label={fontName}
            data-spec="font-option"
            label={
              <div
                className={`${classes.fontOptionText} ${
                  signatureSettings.signatureFont === fontName ? classes.fontOptionTextActive : ''
                }`}
                style={{ fontFamily: SIGNATURE_FONTS[fontName].fontFamily }}
              >
                <div>{signatureSettings.signatureValue}</div>
                <div>{signatureSettings.initialsValue}</div>
              </div>
            }
          />
        ))}
      </RadioGroup>
    </>
  );
};

export default ChooseStyle;
